import React, { useState, useEffect, useMemo, useCallback, createContext } from 'react';
import './App.css';
import 'bootstrap/dist/css/bootstrap.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Bills from './components/Bills/Bills';
import Navigation from './components/Navigation';
import MobileHeader from './components/MobileHeader';
import Sidebar from './components/Sidebar';

import LoginPage from './components/Pages/LoginPage';
import RequirementsPage from './components/Pages/RequirementsPage';
import SettingsPage from './components/Pages/SettingsPage';
import TransfersPage from './components/Pages/TransfersPage';

import './css/Sidebar.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NavigationPreferencesProvider, useNavigationPreferences } from './contexts/NavigationPreferencesContext';
import { getTenant, getSavedTenant } from './utils/tenantUtils';
import { isPhoneViewport } from './utils/homePage';

// Create context for tenant information
const TenantContext = createContext();

function App() {
  // Environment-aware debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('[App.js] Development mode - API Base URL:', process.env.REACT_APP_API_BASE_URL);
  }
  
  const [showSidebar, setShowSidebar] = useState(false);
  const tenant = getTenant(); // Can come from path or subdomain (backward compatibility)
  const location = useLocation();
  
  // Use the context-based auth including loading state
  const { isAuthenticated, checkAuthStatus, loading } = useAuth();
  const { getHomePath, loading: preferencesLoading } = useNavigationPreferences();
  
  // Store the current path before authentication
  const [lastPath, setLastPath] = useState(null);
  
  // Update lastPath when location changes and we're authenticated
  useEffect(() => {
    if (isAuthenticated && location.pathname !== '/login') {
      setLastPath(location.pathname);
      // Also save to session storage as backup
      sessionStorage.setItem('lastPath', location.pathname);
    }
  }, [location.pathname, isAuthenticated]);

  // Check auth status when component mounts
  useEffect(() => {
    checkAuthStatus(true);
  }, [checkAuthStatus]);

  // Redirect to saved tenant on app launch if on root path
  const navigate = useNavigate();
  useEffect(() => {
    // Only redirect if we're on root path and have a saved tenant but no tenant in URL
    if (location.pathname === '/' || location.pathname === '') {
      const savedTenant = getSavedTenant();
      if (savedTenant && !tenant) {
        const platform = isPhoneViewport() ? 'mobile' : 'desktop';
        const homePath = getHomePath(platform) || `/$/${savedTenant}/track`;
        const redirectPath = isAuthenticated ? homePath : `/$/${savedTenant}/login`;
        navigate(redirectPath, { replace: true });
      }
    }
  }, [location.pathname, navigate, tenant, isAuthenticated, loading, getHomePath]);

  // After login (including production tenant-access flow), land on configured home page
  useEffect(() => {
    if (!isAuthenticated || loading || preferencesLoading) return;
    if (sessionStorage.getItem('justLoggedIn') !== '1') return;

    sessionStorage.removeItem('justLoggedIn');
    sessionStorage.removeItem('lastPath');

    const platform = isPhoneViewport() ? 'mobile' : 'desktop';
    const homePath = getHomePath(platform);
    if (homePath && location.pathname !== homePath) {
      navigate(homePath, { replace: true });
    }
  }, [
    isAuthenticated,
    loading,
    preferencesLoading,
    getHomePath,
    location.pathname,
    navigate,
  ]);

  const handleShowSidebar = useCallback(() => {
    setShowSidebar(true);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setShowSidebar(false);
  }, []);

  // Get saved path from storage if not available in state
  const getSavedPath = useCallback(() => {
    const platform = isPhoneViewport() ? 'mobile' : 'desktop';
    const homeFallback = getHomePath(platform) || (tenant ? `/$/${tenant}/track` : '/track');

    if (sessionStorage.getItem('justLoggedIn') === '1') {
      return homeFallback;
    }

    const saved = lastPath || sessionStorage.getItem('lastPath');
    if (tenant && saved && !saved.includes(`/$/${tenant}/`)) {
      return homeFallback;
    }

    return saved || homeFallback;
  }, [lastPath, tenant, getHomePath]);

  // Get page title based on current location
  const getPageTitle = useCallback(() => {
    const path = location.pathname;
    
    // Check for specific bill tracking paths
    if (path.includes('/track/') && path.split('/track/')[1]) {
      return 'Bills'; // Could be enhanced to show specific bank account name
    }
    if (path.includes('/track')) return 'Bills';
    if (path.includes('/transfers')) return 'Transfers';
    if (path.includes('/requirements')) return 'Requirements';
    if (path.includes('/settings')) return 'Settings';
    if (path.includes('/login')) return 'Login';
    return '';
  }, [location.pathname]);

  // Update document title based on current page
  useEffect(() => {
    const pageTitle = getPageTitle();
    if (pageTitle) {
      document.title = `Budgit.lol - ${pageTitle}`;
    } else {
      document.title = 'Budgit.lol';
    }
  }, [getPageTitle]);

  // Memoize ProtectedRoute component
  const ProtectedRoute = useCallback(({ children }) => {
    // Only redirect to login if we've finished checking auth status and user is not authenticated
    if (!loading && !isAuthenticated) {
      const loginPath = tenant ? `/$/${tenant}/login` : '/login';
      return <Navigate to={loginPath} replace />;
    }
    return children;
  }, [isAuthenticated, loading, tenant]);

  // Memoize the sidebar component
  const sidebarComponent = useMemo(() => (
    isAuthenticated && (
      <Sidebar
        show={showSidebar}
        handleClose={handleCloseSidebar}
        onClick={(e) => e.stopPropagation()}
      />
    )
  ), [showSidebar, handleCloseSidebar, isAuthenticated]);

  // Memoize the main content
  const mainContent = useMemo(() => (
    <div
      className={`main-content ${showSidebar ? 'shifted' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <Routes>
        {/* Routes with tenant parameter using /$/ prefix */}
        <Route path="/$/:tenant/login/*" element={isAuthenticated ? <Navigate to={getSavedPath()} /> : <LoginPage />} />
        <Route path="/$/:tenant/register/*" element={<Navigate to="/login" replace />} />
        <Route path="/$/:tenant/track/*" element={<ProtectedRoute><Bills /></ProtectedRoute>} />
        <Route path="/$/:tenant/track/:bankAccountId/*" element={<ProtectedRoute><Bills /></ProtectedRoute>} />
        <Route path="/$/:tenant/transfers/*" element={<ProtectedRoute><TransfersPage /></ProtectedRoute>} />
        <Route path="/$/:tenant/requirements/*" element={<ProtectedRoute><RequirementsPage /></ProtectedRoute>} />
        <Route path="/$/:tenant/settings/*" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

        
        {/* Legacy routes without tenant (backward compatibility) */}
        <Route path="/login" element={isAuthenticated ? <Navigate to={getSavedPath()} /> : <LoginPage />} />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        
        {/* Legacy routes - redirect to tenant-aware routes if tenant exists */}
        <Route path="/track" element={
          tenant ? 
            <Navigate to={`/$/${tenant}/track`} replace /> : 
            <ProtectedRoute><Bills /></ProtectedRoute>
        } />
        <Route path="/track/:bankAccountId" element={
          tenant ? 
            <Navigate to={`/$/${tenant}/track`} replace /> : 
            <ProtectedRoute><Bills /></ProtectedRoute>
        } />
        <Route path="/transfers" element={
          tenant ? 
            <Navigate to={`/$/${tenant}/transfers`} replace /> : 
            <ProtectedRoute><TransfersPage /></ProtectedRoute>
        } />
        <Route path="/requirements" element={
          tenant ? 
            <Navigate to={`/$/${tenant}/requirements`} replace /> : 
            <ProtectedRoute><RequirementsPage /></ProtectedRoute>
        } />

        <Route path="/settings" element={
          tenant ? 
            <Navigate to={`/$/${tenant}/settings`} replace /> : 
            <ProtectedRoute><SettingsPage /></ProtectedRoute>
        } />
        
        {/* Fallback route. If tenant exists, redirect to /$/tenant/login, otherwise /login */}
        <Route path="*" element={
          loading 
            ? null 
            : <Navigate to={isAuthenticated ? getSavedPath() : (tenant ? `/$/${tenant}/login` : "/login")} />
        } />
      </Routes>
    </div>
  ), [showSidebar, isAuthenticated, ProtectedRoute, loading, getSavedPath, tenant]);

  return (
    <TenantContext.Provider value={tenant}>
      <div className={`app-container ${process.env.NODE_ENV === 'development' ? 'dev-environment' : ''}`}>
        {isAuthenticated && <Navigation handleShow={handleShowSidebar} />}
        {isAuthenticated && <MobileHeader handleShow={handleShowSidebar} pageTitle={getPageTitle()} />}
        {sidebarComponent}
        {mainContent}
      </div>
    </TenantContext.Provider>
  );
}

// Wrapper to provide context
function AppWithAuth() {
  return (
    <AuthProvider>
      <NavigationPreferencesProvider>
        <App />
      </NavigationPreferencesProvider>
    </AuthProvider>
  );
}

export { TenantContext };
export default AppWithAuth;